import React from 'react'

import { FoldingPanel } from '../folding-panel'
import { FormPanel } from '../form-panel'

/**
 * @typedef {object} FormFoldingPanelProps
 * @property {object} block - Configuración del bloque de formulario (ver FormPanel).
 * @property {boolean} [block.foldable] - Si es false, el panel no será plegable y mostrará solo el FormPanel.
 * @property {boolean} [block.isFolded] - Estado inicial de plegado cuando foldable es true.
 * @property {string} [block.title] - Título que se muestra en el encabezado del panel plegable.
 * @property {object} data - Objeto con los datos del formulario (pasado a FormPanel).
 * @property {object} [combos] - Objeto con arrays de opciones para selects (pasado a FormPanel).
 * @property {object} fieldList - Mapa de configuraciones de campos (pasado a FormPanel).
 * @property {object} [errors] - Objeto con flags de error por nombre de campo (pasado a FormPanel).
 * @property {boolean} [showErrors] - Si es true, muestra los errores de validación (pasado a FormPanel).
 * @property {React.ReactNode} [extraContent] - Contenido adicional al final del bloque (pasado a FormPanel).
 */

/**
 * Un componente que combina FormPanel con FoldingPanel para crear formularios plegables.
 *
 * Envuelve un FormPanel en un FoldingPanel para permitir que secciones del formulario
 * se puedan expandir y colapsar. Si la configuración indica que el bloque no es plegable
 * (foldable: false), renderiza solo el FormPanel sin el wrapper de FoldingPanel.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const block = {
 *     title: "Datos Personales",
 *     foldable: true,
 *     isFolded: false,
 *     items: [
 *       [{ ref: 'nombre', conf: { xs: 12 } }],
 *       [{ ref: 'apellidos', conf: { xs: 12 } }]
 *     ]
 *   };
 *
 *   const fieldList = {
 *     nombre: {
 *       fromData: (data) => ({ title: 'Nombre', value: data.nombre }),
 *       componentType: 'text',
 *       name: 'nombre'
 *     }
 *   };
 *
 *   return (
 *     <FormFoldingPanel
 *       block={block}
 *       data={{ nombre: 'Juan' }}
 *       fieldList={fieldList}
 *     />
 *   );
 * };
 *
 * @param {FormFoldingPanelProps} props - Las props del componente FormFoldingPanel.
 */
export const FormFoldingPanel = (props) => {
  return props.block.foldable !== false ? (
    <FoldingPanel isExpanded={!props.block.isFolded} title={props.block.title}>
      <FormPanel {...props} />
    </FoldingPanel>
  ) : (
    <FormPanel {...props} />
  )
}

export default FormFoldingPanel
